import { observer } from 'mobx-react-lite';
import {
  Box,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { isDirectory } from '../../../../../types/admin/nodes';
import { Modal } from '@trackman/web-shared-components';

import { ReactComponent as FolderIcon } from 'images/icons/MediaLibrary/Folder.svg';
import { ReactComponent as ArrowDown } from 'images/icons/MediaLibrary/ArrowDown.svg';
import { ReactComponent as ArrowRight } from 'images/icons/MediaLibrary/ArrowRight.svg';
import { useStores } from 'index';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const MoveDialog = observer(({ open, onClose }: Props) => {
  const mediaLibraryStore = useStores().mediaLibraryStore;
  const {
    allNodes,
    moveSelectedTo,
    currentDirectoryId,
    isValidMoveTarget,
    getAncestorIds,
    hasChildDirectories,
  } = mediaLibraryStore;

  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      const ancestors = getAncestorIds(currentDirectoryId);
      // Always expand root and ancestors of current directory
      setExpandedFolders(new Set(['_root', ...ancestors]));
    }
  }, [open, currentDirectoryId, getAncestorIds]);

  const toggleExpand = (folderId: string) => {
    setExpandedFolders(prev =>
      new Set(prev.has(folderId)
        ? [...prev].filter(id => id !== folderId)
        : [...prev, folderId])
    );
  };

  const handleMove = async () => {
    if (selectedTarget) {
      await moveSelectedTo(selectedTarget);
      setSelectedTarget(null);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedTarget(null);
    onClose();
  };

  const renderFolderTree = (parentId: string, depth = 0): JSX.Element[] => {
    return allNodes
      .filter(isDirectory)
      .filter(dir => dir.parentId === parentId && dir.id !== '_trash')
      .map(dir => {
        const isExpanded = expandedFolders.has(dir.id);
        const isDisabled = !isValidMoveTarget(dir.id);
        const isSelected = selectedTarget === dir.id;
        const hasChildren = hasChildDirectories(dir.id);

        return (
          <FolderRow
            key={dir.id}
            id={dir.id}
            name={dir.name}
            depth={depth}
            isSelected={isSelected}
            isDisabled={isDisabled}
            isExpanded={isExpanded}
            onSelect={setSelectedTarget}
            onToggleExpand={toggleExpand}
            showArrow={hasChildren}
          >
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderFolderTree(dir.id, depth + 1)}
            </Collapse>
          </FolderRow>
        );
      });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className="admin-modal"
      title="Move to Folder"
      cancelBtn={{ label: 'Cancel', onClick: handleClose }}
      confirmBtn={{
        label: 'Move',
        onClick: handleMove,
        disabled: !selectedTarget,
      }}
    >
      <div className="modal-body">
        <Typography variant="body2" sx={{ marginBottom: '8px' }}>
          Select destination:
        </Typography>
        <div className="move-dialog__tree">
          <FolderRow
            id="_root"
            name="Home"
            depth={0}
            isSelected={selectedTarget === '_root'}
            isDisabled={!isValidMoveTarget('_root')}
            isExpanded={expandedFolders.has('_root')}
            onSelect={setSelectedTarget}
            onToggleExpand={toggleExpand}
            showArrow={hasChildDirectories('_root')}
          >
            <Collapse in={expandedFolders.has('_root')} timeout="auto" unmountOnExit>
              {renderFolderTree('_root', 1)}
            </Collapse>
          </FolderRow>
        </div>
      </div>
    </Modal>
  );
});

type FolderRowProps = {
  id: string;
  name: string;
  depth: number;
  isSelected: boolean;
  isDisabled: boolean;
  isExpanded?: boolean;
  onSelect: (id: string) => void;
  onToggleExpand?: (id: string) => void;
  showArrow?: boolean;
  children?: React.ReactNode;
};

const FolderRow = ({
  id,
  name,
  depth,
  isSelected,
  isDisabled,
  isExpanded,
  onSelect,
  onToggleExpand,
  showArrow = false,
  children,
}: FolderRowProps) => (
  <div>
    <Box
      display="flex"
      alignItems="center"
      pl={depth * 2}
      py={0.5}
      sx={{
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: isDisabled ? 'transparent' : 'action.hover',
        },
      }}
      onClick={() => {
        if (!isDisabled) onSelect(id);
      }}
    >
      {showArrow ? (
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            if (onToggleExpand) onToggleExpand(id);
          }}
        >
          {isExpanded ? <ArrowDown /> : <ArrowRight />}
        </IconButton>
      ) : (
        <Box sx={{ width: 24, mr: 1 }} />
      )}
      <FolderIcon className="move-dialog__folder-icon" />
      <Typography variant="body2">{name}</Typography>
    </Box>
    {children}
  </div>
);
